import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import {useUpdateAgentMergeConfigurationMutation} from './mutations/use-update-agent-merge-configuration-mutation'
import {useAgentMergeActionPreference} from './use-agent-merge-action-preference'
import {useAgentMergeEnrollmentDisplayState} from './use-agent-merge-enrollment-display-state'
import {
  useAgentMergePageData,
  useInvalidateAgentMergeConfigurationOnAliveUpdate,
} from '../page-data/loaders/use-agent-merge-page-data'
import {
  AgentMergePolicyDisabledError,
  unresolvedAgentMergeEnrollment,
  type AgentMergeAction,
} from '../page-data/payloads/agent-merge'
import {useAgentMergeLoadAnnouncement} from '../components/sections/use-agent-merge-load-announcement'

export type AgentMergeConflictResolutionOptions = {
  currentHeadSha: string
  viewerLogin: string | null
  pullRequestChannel?: string | null
}

export function useAgentMergeConflictResolution({
  currentHeadSha,
  viewerLogin,
  pullRequestChannel,
}: AgentMergeConflictResolutionOptions) {
  const available = isFeatureEnabled('copilot_mission_control_agent_merge_resolve_conflicts')
  useInvalidateAgentMergeConfigurationOnAliveUpdate(available ? pullRequestChannel : null, currentHeadSha)
  const [preferredActions] = useAgentMergeActionPreference()
  const {
    data,
    error: loadError,
    isError: isLoadError,
    refetch,
    claimAnnouncement,
  } = useAgentMergePageData({
    currentHeadSha,
    enabled: available,
  })
  const {mutate, isMutating, errorMessage} = useUpdateAgentMergeConfigurationMutation({currentHeadSha, viewerLogin})
  const enrollmentAttempt = unresolvedAgentMergeEnrollment(data, loadError)
  const isEnrollmentPending = useAgentMergeEnrollmentDisplayState(enrollmentAttempt) === 'pending'
  const policyDisabled = loadError instanceof AgentMergePolicyDisabledError || data?.automationsPolicyDisabled === true
  const readOnly = !enrollmentAttempt && data?.enabled === true && !data.viewerOwnsAssignment
  const alreadyResolving =
    !enrollmentAttempt && data?.enabled === true && data.allowedActions.includes('resolve_conflicts')
  const canEnable =
    available &&
    !!data &&
    !isEnrollmentPending &&
    !isLoadError &&
    !policyDisabled &&
    !readOnly &&
    !isMutating &&
    !alreadyResolving
  useAgentMergeLoadAnnouncement({
    isAvailable: available && !policyDisabled,
    claimAnnouncement,
    error: loadError,
    isError: isLoadError && !isEnrollmentPending,
  })

  return {
    visible: available && (data !== undefined || isLoadError) && !isEnrollmentPending && !policyDisabled && !readOnly,
    canEnable,
    isMutating,
    alreadyResolving,
    errorMessage: errorMessage ?? (isLoadError && !policyDisabled ? loadError.message : null),
    retryLoad: isLoadError ? () => refetch() : undefined,
    enable: () => {
      if (!canEnable) return

      // Keep a live assignment intact, but do not opt a new assignment into review feedback.
      const allowedActions: AgentMergeAction[] = !enrollmentAttempt && data.enabled ? [...data.allowedActions] : []
      const lastSelectionHadFixCI =
        !enrollmentAttempt &&
        viewerLogin !== null &&
        data.ownerLogin === viewerLogin &&
        data.allowedActions.includes('fix_ci')
      if (
        isFeatureEnabled('copilot_mission_control_agent_merge_fix_ci') &&
        (preferredActions?.includes('fix_ci') || lastSelectionHadFixCI) &&
        !allowedActions.includes('fix_ci')
      ) {
        allowedActions.push('fix_ci')
      }
      allowedActions.push('resolve_conflicts')
      const started = mutate({enabled: true, allowedActions})
      announce(started ? 'Saving Agent Merge settings' : 'Agent Merge settings update queued')
    },
  }
}
